import React from 'react';
import { useNavigate } from 'react-router-dom';
import ListModal from './ListModal';
import { fmtDate } from '../lib/when';

const slug = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, '-');

/**
 * The list behind a number. Opened by a stat tile or a chart segment; each row
 * opens the ticket. The dialog shell (Escape, click-outside, focus trap) lives
 * in ListModal.
 *
 * `onOpen` exists because /tickets/:id is a STAFF route:
 *
 *   element={isStaff() || isAdmin() ? <TicketDetail /> : <Navigate to="/contractor-login" />}
 *
 * so a contractor pressing a row in their own portal was navigated out of the
 * portal and onto the login page. From the outside that is "I cannot open my
 * own ticket" - which is how Gordon reported it - and nothing about the modal
 * said it only worked for staff.
 *
 * So the caller may say what opening a ticket means where it is being used.
 * Left out, it still navigates, which is right everywhere staff see it.
 */
export default function TicketListModal({ title, subtitle, tickets, onClose, onOpen,
  loading = false, error = false }) {
  const navigate = useNavigate();
  const hasHours = tickets.some((t) => t.hours !== undefined && t.hours !== null);
  const totalHours = hasHours
    ? tickets.reduce((n, t) => n + (Number(t.hours) || 0), 0)
    : null;

  const open = (ticket) => {
    onClose();
    if (onOpen) onOpen(ticket);
    else navigate(`/tickets/${ticket.id}`);
  };

  return (
    <ListModal
      title={title}
      subtitle={subtitle}
      count={tickets.length}
      noun="ticket"
      onClose={onClose}
    >
      {/*
        * THREE DIFFERENT FACTS, NOT ONE.
        *
        * This said "Nothing here for this selection" whenever the list was
        * empty, which was fine while every caller filtered a list it already
        * held - empty meant empty. The four hours tiles FETCH their list, so
        * the same empty array now also means "still asking" and "the request
        * failed", and reporting either of those as "there are none" is a
        * wrong answer rather than a missing one.
        */}
      {loading ? (
        <p className="tlm-empty">Looking…</p>
      ) : error ? (
        <p className="tlm-empty" role="alert">
          Could not load that list. The figure on the tile is still right —
          this is the breakdown that failed.
        </p>
      ) : tickets.length === 0 ? (
        <p className="tlm-empty">Nothing here for this selection.</p>
      ) : (
        <table className="tlm-table">
          <thead>
            {/* `hours` only arrives from the four hours tiles. The column is
                what lets somebody check that the rows add up to the number
                they pressed - a breakdown nobody can reconcile is decoration. */}
            <tr>
              <th>Ticket</th>
              <th>Title</th>
              {hasHours && <th className="tlm-hours-h">Hours</th>}
              <th>Status</th>
              <th>Priority</th>
              <th>Contractor</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr
                key={t.id}
                className="tlm-row"
                role="link"
                tabIndex={0}
                title={`Open ${t.ticket_number}`}
                onClick={() => open(t)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(t); }
                }}
              >
                <td className="tlm-number">{t.ticket_number}</td>
                <td className="tlm-ticket-title">{t.title}</td>
                {hasHours && (
                  <td className="tlm-hours">
                    {(Number(t.hours) || 0).toFixed(2).replace(/\.00$/, '')}
                  </td>
                )}
                <td>
                  <span className="ticket-status-badge" data-status={slug(t.status || t.status_name)}>
                    {t.status || t.status_name || '—'}
                  </span>
                </td>
                <td>
                  <span className="priority-badge" data-priority={slug(t.priority || t.priority_name)}>
                    {t.priority || t.priority_name || '—'}
                  </span>
                </td>
                <td className="tlm-contractor">{t.contractor_name || '—'}</td>
                <td className="tlm-date">
                  {t.created_at ? fmtDate(t.created_at) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          {/* The sum, stated. This is the whole reason the Hours column is
              here: the tile says 41.5 and this says 41.5, so the breakdown
              can be checked rather than believed. */}
          {hasHours && (
            <tfoot>
              <tr className="tlm-total">
                <td colSpan={2}>Total</td>
                <td className="tlm-hours">
                  {totalHours.toFixed(2).replace(/\.00$/, '')}
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </ListModal>
  );
}
